/**
 * Error mapping for the reservations surfaces. A failed LOAD renders through the
 * shared `ScreenError` (DNKADM-0440); this file adds only the feature's actions:
 *  - `toReservationActionErrorMessage` maps a failed cancel/reschedule to an inline
 *    staff-facing line by typed `ApiError.code`;
 *  - `toReassignErrorView` maps a failed reassignment (DNKADM-0211) HTTP-status-first
 *    to its message PLUS the dialog's three recovery decisions.
 *
 * Kept tiny and framework-free so both the standalone page and the per-member
 * panel render the same states without duplicating the switch.
 */
import { ApiError } from '@/api/errors';
import { moneyRefusalMessage } from '@/api/moneyRefusal';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import { declineReasonLineFromError } from '@/utils/declineReason';
import { REASSIGN_ERROR_CODES } from '@/api/reservations';

/**
 * A reservations-window load the server rejected as malformed (`400`): the one rejected
 * load that gets its own "Invalid date window" copy on top of the shared `ScreenError`
 * (a `409`/`422` keeps the generic line with the server's message).
 */
export function isInvalidWindowError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'VALIDATION';
}

export const INVALID_WINDOW_MESSAGE =
  'The reservations window is invalid. Choose a valid start and end date.';

/**
 * Inline message for a failed cancel/reschedule. Maps the typed `ApiError.code`:
 *  - `VALIDATION` (400) → blank reason / unknown directive / malformed body
 *    (prefer the server's own message when present);
 *  - `FORBIDDEN` (403) → the booking is outside the caller's tenant;
 *  - `CONFLICT` (409) → upstream rejected the change (e.g. slot taken);
 *  - `NOT_FOUND` (404) → dink-api answers a cancel/reschedule of a booking that is no
 *    longer open (cancelled, checked in, marked no-show) with a bare "Reservation not
 *    found"; say what that means for the desk (DNKADM-0426);
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles refresh/redirect).
 * Anything else falls back to the server message, then a generic line.
 */
export function toReservationActionErrorMessage(error: unknown): string {
  // DNKAPI-0670's money refusals (MANAGER_PIN_*, STAFF_NOT_ALLOWED) say what to do next.
  const refusal = moneyRefusalMessage(error);
  if (refusal !== null) return refusal;
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'That change isn’t valid — check the reason and the new times.';
      case 'FORBIDDEN':
        return 'You’re not authorized to change this booking.';
      case 'CONFLICT':
        return msg ?? 'That change conflicts with another booking. Try a different time.';
      case 'NOT_FOUND':
        return 'This booking can’t be changed any more. It may already be cancelled, checked in or marked no-show. Close this and check its status.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The change could not be completed. Please try again.';
}

/**
 * Shared copy for dink-api's four availability codes (DNKAPI-0444 split the
 * overloaded `SERVICE_INACTIVE` into these; all 409) — DNKADM-0289.
 *
 * These OVERRIDE the server's message instead of this file's usual `msg ?? ourCopy`
 * deferral. The rule: defer to the server's message when it is contextual (carries
 * per-request detail we cannot reconstruct); override it when it is a fixed
 * constant we can state better. dink's messages for all four are hardcoded
 * constants ("Service is not available", …) with no per-request detail, so
 * deferring buys nothing and costs clarity. Revisit if dink ever makes these
 * messages carry per-request detail.
 *
 * `booking` phrasing is shared by the booking dialogs (`toBookingErrorMessage`)
 * AND the pre-submit `AvailabilitySlotPicker` (`availabilityCodeMessage`), so the
 * availability read and the submit failure say the same thing; `reassign`
 * phrasing names the already-made booking (`toReassignErrorView`).
 */
const AVAILABILITY_CODE_COPY = {
  SERVICE_INACTIVE: {
    booking: 'This service is inactive, so it can’t be booked.',
    reassign: 'This service is inactive, so its bookings can’t be reassigned.',
  },
  VARIATION_INACTIVE: {
    booking: 'That service option is no longer active. Pick a different option.',
    reassign: 'This booking’s service option is no longer active, so it can’t be reassigned.',
  },
  VARIATION_NOT_FOR_SERVICE: {
    booking: 'That option belongs to a different service. Re-select the service and option.',
    reassign: 'This booking’s option belongs to a different service, so it can’t be reassigned.',
  },
  LOCATION_INACTIVE: {
    booking: 'This location is inactive, so it can’t take bookings.',
    reassign: 'This booking’s location is inactive, so it can’t be reassigned.',
  },
} as const;

/**
 * The `booking` copy for an error carrying one of the four availability codes
 * (see {@link AVAILABILITY_CODE_COPY}), or `null` for anything else — callers
 * fall through to their own message source.
 */
/**
 * The admin availability route's documented failures (DNKADM-0390 U2), HTTP-status-first:
 * `404` — the target contact is unknown or belongs to another tenant (the guard fails closed);
 * `403` — the caller is not an OWNER/ADMIN. Null for anything else (the availability codes and
 * the server message follow).
 */
export function adminAvailabilityMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 404) {
    return 'This member couldn’t be found for this location, so their available times can’t be checked.';
  }
  if (error.status === 403) {
    return 'Your login can’t check a member’s available times. Ask an owner or admin.';
  }
  return null;
}

export function availabilityCodeMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  return code !== undefined && code in AVAILABILITY_CODE_COPY
    ? AVAILABILITY_CODE_COPY[code as keyof typeof AVAILABILITY_CODE_COPY].booking
    : null;
}

/**
 * What the reassignment dialog should DO about a failed
 * `PUT …/{uuid}/assignment` (DNKADM-0211 U2) — the message plus the three
 * recovery decisions, so the dialog itself carries no error taxonomy.
 */
export interface ReassignErrorView {
  /** The inline, staff-facing line (brand-scrubbed server text where it helps). */
  message: string;
  /** Nothing the admin can retry from this dialog — Save is disabled. */
  blocked: boolean;
  /** The pools may be stale — re-read `assignment-options` and let them pick again. */
  refetchOptions: boolean;
  /** The calendar's copy of this booking is out of date — refresh the window. */
  stale: boolean;
}

/**
 * Map a failed reassignment **HTTP-status-first** (the code only disambiguates
 * WITHIN a status), per the shipped DNKAPI-0356/0357 contract:
 *  - `400` — the pick isn't an assignable member of the service's pool (or the
 *    body was rejected): recoverable → re-fetch the options, keep Save live.
 *  - `403 FORBIDDEN_TENANT` — the fail-closed tenant guard, which covers a
 *    foreign **and an unknown** uuid. Generic copy on purpose (it must not
 *    confirm whether the id exists) and nothing to retry.
 *  - `404` — the booking is **terminal** (cancelled/gone), NOT merely unknown
 *    (that is the 403 above): inform, refresh the calendar, stop offering Save.
 *  - `409` — `RESERVATION_PAST` (past the appointment + grace: check in / mark
 *    no-show instead), the not-CONFIRMED case (only a confirmed booking is
 *    reassignable → the calendar is behind), or one of the four availability
 *    codes (DNKAPI-0444 — service/variation/location inactive, variation-service
 *    mismatch). All of them are dead ends for this dialog.
 * The dialog stays OPEN in every case so the admin keeps their context.
 */
export function toReassignErrorView(error: unknown): ReassignErrorView {
  // An empty/blank server `message` is treated as absent, so a code-only envelope
  // still gets this mapper's own copy rather than rendering a blank alert.
  const raw = serverMessage(error);
  const msg = raw !== null && raw.trim().length > 0 ? raw : null;
  const code = v2ErrorCode(error);
  const status = error instanceof ApiError ? error.status : 0;

  switch (status) {
    case 400:
      return {
        message:
          code === REASSIGN_ERROR_CODES.staffNotAssignable
            ? (msg ?? 'That staff member can’t be assigned to this booking.')
            : code === REASSIGN_ERROR_CODES.resourceNotAssignable
              ? (msg ?? 'That bay can’t be assigned to this booking.')
              : (msg ?? 'That reassignment isn’t valid for this booking.'),
        blocked: false,
        refetchOptions: true,
        stale: false,
      };
    case 401:
      return {
        message: 'Your session expired. Please sign in again to continue.',
        blocked: true,
        refetchOptions: false,
        stale: false,
      };
    case 403:
      return {
        message: 'You’re not authorized to change this booking.',
        blocked: true,
        refetchOptions: false,
        stale: false,
      };
    case 404:
      return {
        message:
          'This booking is no longer active, so it can’t be reassigned. The calendar has been refreshed.',
        blocked: true,
        refetchOptions: false,
        stale: true,
      };
    case 409:
      if (code === REASSIGN_ERROR_CODES.past) {
        return {
          message:
            'This booking has already started, so it can no longer be reassigned. Check the member in or mark a no-show instead.',
          blocked: true,
          refetchOptions: false,
          stale: false,
        };
      }
      if (code === REASSIGN_ERROR_CODES.notConfirmed) {
        return {
          message:
            'Only a confirmed booking can be reassigned. The calendar has been refreshed.',
          blocked: true,
          refetchOptions: false,
          stale: true,
        };
      }
      if (
        code === REASSIGN_ERROR_CODES.serviceInactive ||
        code === REASSIGN_ERROR_CODES.variationInactive ||
        code === REASSIGN_ERROR_CODES.locationInactive ||
        // Unreachable from this dialog by construction (see REASSIGN_ERROR_CODES),
        // mapped defensively as the same dead end. No recovery affordance on
        // purpose: re-fetching assignment options returns staff/resource pools and
        // cannot change the service/variation pairing.
        code === REASSIGN_ERROR_CODES.variationNotForService
      ) {
        // DNKADM-0289: our copy overrides dink's fixed constant — the rule lives
        // at AVAILABILITY_CODE_COPY.
        return {
          message: AVAILABILITY_CODE_COPY[code].reassign,
          blocked: true,
          refetchOptions: false,
          stale: false,
        };
      }
      if (code === 'RESERVATION_CHANGED') {
        // DNKAPI-0636 D-04: another write changed the booking between read and lock and
        // nothing was written. The dialog's copy of it is stale, so reload the calendar.
        return {
          message:
            'This booking was changed by someone else. The calendar has been refreshed; reopen it and try again.',
          blocked: true,
          refetchOptions: false,
          stale: true,
        };
      }
      return {
        message: msg ?? 'That reassignment conflicts with the booking’s current state.',
        blocked: false,
        refetchOptions: false,
        stale: false,
      };
    default:
      return {
        message: msg ?? 'The reassignment could not be completed. Please try again.',
        blocked: false,
        refetchOptions: false,
        stale: false,
      };
  }
}

/**
 * Whether a thrown booking error is a missing-`*_REQUIRED` validation (DNKADM-0061 U2).
 * A `MEMBER_SELECTS` service without the picked id returns a `400 STAFF_REQUIRED` /
 * `RESOURCE_REQUIRED` — the create flow highlights the missing picker. Returns which
 * dimension is required, or null when the error is something else.
 */
export function requiredPickerFromError(error: unknown): 'staff' | 'resource' | null {
  const code = v2ErrorCode(error);
  if (code === 'STAFF_REQUIRED') return 'staff';
  if (code === 'RESOURCE_REQUIRED') return 'resource';
  return null;
}

/**
 * Single-source booking copy for the codes where WE own the message (DNKADM-0291,
 * extending the DNKADM-0289 rule beyond the availability codes; those four keep
 * their home in {@link AVAILABILITY_CODE_COPY}).
 *
 * These OVERRIDE the server's message: dink's message for each is a fixed constant
 * (or, for `NO_ACTIVE_MEMBERSHIP`, contextual-but-useless — it interpolates a raw
 * contact id, noise that leaks an internal identifier), several are member-voiced
 * ("…requires you to select…") which is wrong in an admin console, and ours carry
 * the actionable next step. Under the old `msg ?? ourCopy` shape this copy was
 * unreachable — dink always sends a non-blank message (backlog 0026).
 *
 * `SLOT_TAKEN` / `SLOT_CONTENDED` / `OVERLAPPING_RESERVATION` are deliberately
 * three DIFFERENT messages (the bug this audit found): taken = someone else got
 * it, pick another time; contended = a concurrent request holds it, RETRYING may
 * succeed (never "pick another time"); overlapping = THIS member's own calendar
 * conflicts — the slot itself is fine.
 */
const BOOKING_CODE_COPY = {
  SLOT_TAKEN: 'That slot was just taken by another booking. Pick another available time.',
  SLOT_CONTENDED:
    'Another booking request is holding this slot. Try again — it may go through.',
  OVERLAPPING_RESERVATION:
    'This member already has a booking that overlaps this time. Pick a non-conflicting time.',
  RESOURCE_FULL: 'That resource is fully booked for this slot. Pick another time.',
  STAFF_UNAVAILABLE:
    'The selected staff isn’t available for this slot. Pick another time or person.',
  NO_ACTIVE_MEMBERSHIP:
    'This booking type is member-only — the member needs an active membership. Book them on a credit-cost slot, or sell a membership.',
  OUTSIDE_BOOKING_WINDOW: 'That time is outside the bookable window for this service.',
  OUTSIDE_LOCATION_HOURS:
    'That time is outside location hours. After-hours access requires a membership.',
  SERVICE_HAS_NO_ASSIGNABLE_STAFF:
    'This service has no assignable staff configured. Add eligible staff first.',
  SERVICE_HAS_NO_ASSIGNABLE_RESOURCE:
    'This service has no assignable resource configured. Add eligible resources first.',
  STAFF_REQUIRED: 'Choose a staff member for this booking.',
  RESOURCE_REQUIRED: 'Choose a resource for this booking.',
  // DNKADM-0402 U3 (DNKAPI-0559). Both OVERRIDE the server's message: dink's copy is a
  // fixed constant, and the desk needs the next step, not the refusal's wording.
  PAYMENT_DECLINED: 'The card was declined. Nothing was booked.',
  PAYMENT_ACTION_REQUIRED:
    'This card needs the customer to approve the payment. Use another card or take cash.',
} as const;

/**
 * Clear, staff-facing message for a failed create-on-behalf (DNKADM-0061 U2). Maps the
 * full §4.2 booking error catalog off the fine-grained v2 `details.code`
 * (`v2ErrorCode`). DNKADM-0291: most codes now OVERRIDE the server's message with
 * {@link BOOKING_CODE_COPY} / {@link AVAILABILITY_CODE_COPY}; only the codes whose
 * server message interpolates per-request specifics defer (`msg ?? fallback`), and the
 * `default:` still defers — an unrecognised code is exactly where the server's message
 * is the only information available.
 */
export function toBookingErrorMessage(error: unknown): string {
  const msg = serverMessage(error);
  const code = v2ErrorCode(error);
  switch (code) {
    // DNKADM-0291: our copy, always — see BOOKING_CODE_COPY for the rule and the
    // three-way slot-conflict split.
    case 'SLOT_TAKEN':
    case 'SLOT_CONTENDED':
    case 'OVERLAPPING_RESERVATION':
    case 'RESOURCE_FULL':
    case 'STAFF_UNAVAILABLE':
    case 'NO_ACTIVE_MEMBERSHIP':
    case 'OUTSIDE_BOOKING_WINDOW':
    case 'OUTSIDE_LOCATION_HOURS':
    case 'SERVICE_HAS_NO_ASSIGNABLE_STAFF':
    case 'SERVICE_HAS_NO_ASSIGNABLE_RESOURCE':
    case 'STAFF_REQUIRED':
    case 'RESOURCE_REQUIRED':
    case 'PAYMENT_ACTION_REQUIRED':
      return BOOKING_CODE_COPY[code];
    // DNKAPI-0676: the staff 402 may say why the card was declined; OTHER/unknown keep our line.
    case 'PAYMENT_DECLINED': {
      const reasonLine = declineReasonLineFromError(error);
      return reasonLine !== null ? `${reasonLine} Nothing was booked.` : BOOKING_CODE_COPY[code];
    }
    // DEFER (dink's message interpolates per-request specifics we cannot
    // reconstruct — do NOT "finish the job" by overriding these):
    // INSUFFICIENT_CREDITS names the bucket and the shortfall; DAILY_CAP_EXCEEDED
    // names the bucket; INVALID_SLOT_TIME names the offending field (a 400 that
    // should rarely reach an operator).
    case 'INSUFFICIENT_CREDITS':
      return msg ?? 'No credits for this booking — sell a package or take payment.';
    case 'DAILY_CAP_EXCEEDED':
      return msg ?? 'This booking would exceed the member’s daily cap.';
    case 'INVALID_SLOT_TIME':
      return msg ?? 'That start time isn’t a valid slot. Pick an available time.';
    // DEFER — but for a different reason (DNKADM-0291): STAFF_CAP_EXCEEDED is
    // declared in dink-api's ReservationErrorCode yet has NO throw site anywhere in
    // its source — the cap check folds into the staff-availability free-set, so an
    // over-cap staffer surfaces as STAFF_UNAVAILABLE instead. No server message
    // exists to classify; deferring shows whatever dink ships first if the code
    // ever goes live. Re-classify when a real throw site lands.
    case 'STAFF_CAP_EXCEEDED':
      return msg ?? 'The selected staff has reached their booking cap for this period.';
    // DNKADM-0289 (DNKAPI-0444): the four availability codes OVERRIDE the server's
    // message rather than defer (`msg ??`) — dink's message for these is a fixed
    // constant, not contextual detail. The rule and the copy live at
    // AVAILABILITY_CODE_COPY.
    case 'SERVICE_INACTIVE':
    case 'VARIATION_INACTIVE':
    case 'VARIATION_NOT_FOR_SERVICE':
    case 'LOCATION_INACTIVE':
      return AVAILABILITY_CODE_COPY[code].booking;
    default:
      return msg ?? 'The booking could not be created. Please try again.';
  }
}

/**
 * Copy for a failed **Take payment** (DNKADM-0402 U5) — one booking, one charge.
 *
 * **Nothing is being BOOKED on this surface**, so this does NOT fall through to
 * {@link toBookingErrorMessage}'s terminal line ("The booking could not be created"),
 * which would answer a question nobody asked and imply the booking is gone. The desk's
 * question here is only ever *did the money move?* — so the unmapped case defers to the
 * server's own message, and says {@link TAKE_PAYMENT_FALLBACK} when there is none (a
 * network drop mid-charge, or a code-only envelope). The codes that reach it in practice
 * are `DUPLICATE_SUBMIT` / `ONLINE_PAYMENT_UNAVAILABLE` (409) and `FORBIDDEN_TENANT`
 * (403 — this route answers 403, not 404, for an unknown uuid), all of which carry a
 * server message; the booking catalog's slot/credit codes cannot occur, because no
 * booking gate runs on a payment.
 *
 * The three mapped codes are the ones WE own the words for: a decline says what actually
 * did not happen, `PAYMENT_ACTION_REQUIRED` keeps the shared booking copy (the desk's
 * next step is the same either way), and `RESERVATION_NOT_UNPAID` — someone took the
 * money first — gets its own line.
 */
export function toTakePaymentErrorMessage(error: unknown): string {
  const code = v2ErrorCode(error);
  if (code === 'PAYMENT_DECLINED') {
    // DNKAPI-0676: the staff 402 may say why the card was declined; OTHER/unknown keep our line.
    const reasonLine = declineReasonLineFromError(error);
    return reasonLine !== null
      ? `${reasonLine} Nothing was charged.`
      : 'The card was declined. Nothing was charged.';
  }
  if (code === 'RESERVATION_NOT_UNPAID') return 'This booking was already paid.';
  if (code === 'PAYMENT_ACTION_REQUIRED') return BOOKING_CODE_COPY.PAYMENT_ACTION_REQUIRED;
  return serverMessage(error) ?? TAKE_PAYMENT_FALLBACK;
}

/**
 * The terminal line when a take-payment failure carries no server message at all. It
 * neither claims the money moved nor claims it did not — the console cannot know, and
 * the one safe instruction is to look before charging again.
 */
export const TAKE_PAYMENT_FALLBACK =
  'The payment could not be taken. Check the payment before trying again.';

/** Whether a failure is dink-api's `409 RESERVATION_NOT_UNPAID` (DNKAPI-0559 U5). */
export function isAlreadyPaidRefusal(error: unknown): boolean {
  return v2ErrorCode(error) === 'RESERVATION_NOT_UNPAID';
}

/**
 * Whether a Check payment failure is dink-api's `409 RESERVATION_PAYMENT_NOT_STRANDED`
 * (DNKAPI-0563) — the booking has no payment in progress any more. A normal answer, not an
 * error: the caller re-reads the booking and shows what it is now.
 */
export function isPaymentNotStrandedRefusal(error: unknown): boolean {
  return v2ErrorCode(error) === 'RESERVATION_PAYMENT_NOT_STRANDED';
}

/**
 * The inline line for a Check payment call that failed outright (a pay-api fault, a `403`,
 * a dropped connection). The server's own message when there is one; otherwise a line that
 * claims nothing about the money, because nothing was recorded.
 */
export function toCheckPaymentErrorMessage(error: unknown): string {
  return serverMessage(error) ?? 'The payment could not be checked. Try again in a moment.';
}

/**
 * Whether a Close out failure is dink-api's `409 RESERVATION_CLOSE_OUT_PAYMENT_INVALID`
 * (DNKAPI-0565) — the payment id staff named fails a check. Its message says which check,
 * and is shown next to the payment id field.
 */
export function isCloseOutPaymentInvalid(error: unknown): boolean {
  return v2ErrorCode(error) === 'RESERVATION_CLOSE_OUT_PAYMENT_INVALID';
}

/**
 * Whether a Close out failure is a `403`: a session dink-api does not let close out (it is
 * OWNER/ADMIN only), or a booking outside the tenant. Either way the console stops offering
 * the control; the role check is the server's.
 */
export function isCloseOutForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * The inline line for any other Close out failure (a `400`, a pay-api fault, a dropped
 * connection). The server's own message when there is one; otherwise a line that claims
 * nothing was recorded, because nothing was.
 */
export function toCloseOutPaymentErrorMessage(error: unknown): string {
  return serverMessage(error) ?? 'The payment could not be closed out. Try again in a moment.';
}
